import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CashDisplay, RunwayIndicator, ViewToggle } from "../../atoms";
import type { ViewMode } from "../../atoms";
import CashFlowBarChart from "../../charts/CashFlowBarChart";
import RevenueBreakdownChart from "../../charts/RevenueBreakdownChart";
import ExpenseBreakdownChart from "../../charts/ExpenseBreakdownChart";
import ExpenseTypeBreakdownChart from "../../charts/ExpenseTypeBreakdownChart";
import CashFlowHealthChart from "../../charts/CashFlowHealthChart";
import CashFlowStatusChart from "../../charts/CashFlowStatusChart";
import QuarterlyCashFlowChart from "../../charts/QuarterlyCashFlowChart";
import RunwayProjectionChart from "../../charts/RunwayProjectionChart";
import GrowthPlanChart from "../../charts/GrowthPlanChart";
import CustomerGrowthChart from "../../charts/CustomerGrowthChart";
import { EditViewGrid } from "../../molecules/EditViewGrid";
import type { FinancialVisualizationDashboardProps } from "./FinancialVisualizationDashboard.types";

import { mapChartDataToStartup } from "../../../utils/chart/adapter.utils";
import { teamAPI } from "@/services/api/team.service";

const FinancialVisualizationDashboard: React.FC<
  FinancialVisualizationDashboardProps
> = ({
  currentSnapshot,
  breakdownSnapshot: _breakdownSnapshot,
  data,
  visibleDateRange,
  expenses = [],
  revenues = [],
  events = [],
  customerProjections = [],
  subscriptionTiers = [],
  capacityGroups = [],
  teamMembers: teamMembersProp,
  customerGrowthScenarios = [],
  acquisitionCostModels = [],
  startupId,
  rawExpenses,
  rawRevenues,
  rawEvents,
  onExpenseClick,
  onRevenueClick,
  onEventClick,
  onCustomerProjectionClick,
  onSubscriptionTierClick,
  onCapacityGroupClick,
  onAddExpense,
  onAddRevenue,
  onAddEvent,
  onIncorporateGrowthPlan,
  onAddCustomerProjection,
  onAddCapacityGroup,
  onRevisionReviseAgain,
  readOnly = false,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("timeline");

  const { data: fetchedTeamMembers = [] } = useQuery({
    queryKey: ["teamMembers", startupId],
    queryFn: () => teamAPI.listTeamMembers(startupId || ""),
    enabled: Boolean(startupId) && teamMembersProp === undefined && !readOnly,
  });
  const teamMembers = teamMembersProp ?? fetchedTeamMembers;

  // Use currentSnapshot date if available for timeline-aware breakdown charts
  const breakdownDate = currentSnapshot?.date || new Date();

  // Map the burndown chart data to startup-shaped data to satisfy chart component typings
  // Memoize to prevent unnecessary re-renders of chart components
  const startupShapedData = useMemo(() => {
    const customerProjectionData =
      data.customerProjectionData ??
      ((customerProjections && customerProjections.length > 0) ||
      (subscriptionTiers && subscriptionTiers.length > 0)
        ? {
            projections: customerProjections || [],
            subscriptionTiers: subscriptionTiers || [],
            growthScenarios: customerGrowthScenarios || [],
            acquisitionCostModels: acquisitionCostModels || [],
          }
        : undefined);

    const dataWithCustomerProjections = {
      ...data,
      customerProjectionData,
    };

    return {
      startupData: mapChartDataToStartup(dataWithCustomerProjections as any),
      customerProjectionData,
    };
  }, [
    data,
    customerProjections,
    subscriptionTiers,
    customerGrowthScenarios,
    acquisitionCostModels,
  ]);

  return (
    <div className="border-t border-white/10 bg-surface p-4 sm:p-6">
      {/* Available Cash Display */}
      <div className="flex items-center justify-center mb-4">
        <div className="text-center">
          <CashDisplay cashBalance={currentSnapshot?.cashBalance || 0} />
          <RunwayIndicator runway={currentSnapshot?.runway || Infinity} />
        </div>
      </div>

      {/* View Toggle */}
      <div className="mb-6 flex justify-center px-2">
        <ViewToggle
          currentView={viewMode}
          onViewChange={setViewMode}
        />
      </div>

      {viewMode === "timeline" ? (
        <>
          {/* Cash Flow Bar Chart - Moved below */}
          <div className="w-full mb-8">
            <CashFlowBarChart
              data={startupShapedData.startupData}
              visibleDateRange={visibleDateRange}
              currentDate={breakdownDate}
              height={200}
            />
          </div>

          {/* Analytics Dashboard Grid */}
          <div className="space-y-6">
            {/* Pie Charts Row - Responsive grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {/* Revenue Breakdown */}
              <div className="sh-panel h-80 md:h-72 xl:h-80 p-2">
                <RevenueBreakdownChart
                  revenues={(currentSnapshot?.activeRevenues || []).map(
                    (r: any) => ({
                      ...r,
                      id: r.id,
                      name: r.name,
                      monthlyAmount: r.monthlyAmount ?? r.amount ?? 0,
                      type: (r.type ?? r.category) as any,
                    })
                  )}
                  events={rawEvents || []}
                  customerProjectionData={
                    startupShapedData.customerProjectionData
                  }
                  currentDate={breakdownDate}
                />
              </div>

              {/* Expense Breakdown */}
              <div className="sh-panel h-80 md:h-72 xl:h-80 p-2">
                <ExpenseBreakdownChart
                  expenses={(currentSnapshot?.activeExpenses || []).map(
                    (e: any) => ({
                      ...e,
                      id: e.id,
                      name: e.name,
                      monthlyAmount: e.monthlyAmount ?? e.oneTimeAmount ?? 0,
                      category: e.category,
                    })
                  )}
                  events={rawEvents || []}
                  customerProjectionData={
                    startupShapedData.customerProjectionData
                  }
                  currentDate={breakdownDate}
                />
              </div>

              {/* Expense Type Breakdown (Fixed vs Variable) */}
              <div className="sh-panel h-80 md:h-72 xl:h-80 p-2">
                <ExpenseTypeBreakdownChart
                  expenses={(currentSnapshot?.activeExpenses || []).map(
                    (e: any) => ({
                      ...e,
                      id: e.id,
                      name: e.name,
                      monthlyAmount: e.monthlyAmount ?? e.oneTimeAmount ?? 0,
                      expenseType: e.expenseType || "fixed",
                    })
                  )}
                  currentDate={breakdownDate}
                />
              </div>

              {/* Cash Flow Health */}
              <div className="sh-panel h-80 md:h-72 xl:h-80 p-2">
                <CashFlowHealthChart
                  currentSnapshot={currentSnapshot}
                  currentDate={breakdownDate}
                />
              </div>
            </div>

            {/* Cash Flow Status - Full Width */}
            <div className="space-y-6">
              <CashFlowStatusChart
                data={startupShapedData.startupData}
                visibleDateRange={visibleDateRange}
                height={320}
              />
              <QuarterlyCashFlowChart
                data={startupShapedData.startupData}
                visibleDateRange={visibleDateRange}
                height={360}
              />
            </div>

            {/* Runway Projection - Full Width */}
            <div className="sh-panel p-2">
              <RunwayProjectionChart
                data={startupShapedData.startupData}
                visibleDateRange={visibleDateRange}
                height={350}
              />
            </div>

            {/* Growth plan — revenue ramp, profit, and cash toward break-even */}
            {currentSnapshot ? (
              <GrowthPlanChart
                chartData={startupShapedData.startupData}
                currentSnapshot={currentSnapshot}
                height={380}
                hasCustomerProjections={
                  (customerProjections?.length ?? 0) > 0
                }
                onIncorporateGrowthPlan={
                  readOnly ? undefined : onIncorporateGrowthPlan
                }
                onAddCustomerProjection={
                  readOnly ? undefined : onAddCustomerProjection
                }
              />
            ) : null}

            {/* Customer Growth Chart - Full Width (only shows if customer projections exist) */}
            {startupShapedData.customerProjectionData && (
              <div className="sh-panel p-2">
                <CustomerGrowthChart
                  customerProjectionData={startupShapedData.customerProjectionData}
                  visibleDateRange={visibleDateRange}
                  height={350}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        /* Grid view */
        <div className="mt-4">
          <EditViewGrid
            startupId={startupId || ""}
            expenses={rawExpenses || expenses}
            revenues={rawRevenues || revenues}
            events={rawEvents || events}
            customerProjections={customerProjections}
            subscriptionTiers={subscriptionTiers}
            capacityGroups={
              capacityGroups.length > 0
                ? capacityGroups
                : data.customerProjectionData?.capacityGroups
            }
            teamMembers={teamMembers}
            onExpenseClick={readOnly ? undefined : onExpenseClick}
            onRevenueClick={readOnly ? undefined : onRevenueClick}
            onEventClick={readOnly ? undefined : onEventClick}
            onCustomerProjectionClick={
              readOnly ? undefined : onCustomerProjectionClick
            }
            onSubscriptionTierClick={readOnly ? undefined : onSubscriptionTierClick}
            onCapacityGroupClick={readOnly ? undefined : onCapacityGroupClick}
            onAddExpense={readOnly ? undefined : onAddExpense}
            onAddRevenue={readOnly ? undefined : onAddRevenue}
            onAddEvent={readOnly ? undefined : onAddEvent}
            onAddCapacityGroup={readOnly ? undefined : onAddCapacityGroup}
            onRevisionReviseAgain={readOnly ? undefined : onRevisionReviseAgain}
            readOnly={readOnly}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

export default FinancialVisualizationDashboard;
